import { AccordionItem, AccordionItemContent } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useContext, useEffect, useRef, useState } from 'react'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import useStyles from 'src/Libs/styles'
import MinimalVersionFormContent from './form-content/MinimalVersionFormContent'
import { DynamicPricesModel } from '../../types'
import FormikWithExitHandler from 'src/Libs/form/components/FormikWithExitHandler'
import { useScrollToHash } from 'src/Libs/navigation/hooks'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { useWallboxes } from 'src/Libs/wallboxes'
import useCheckInstallationLocation from 'src/Libs/dynamic-power-prices/hooks/use-check-installation-location'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { COUNTRY_NOT_AVAILABLE_KEY, MASTER_IS_OFFLINE_KEY } from '../../const'
import { FormBlankView, FormOptIn } from './form-content'
import { Box } from '@mui/material'
import { usePrevious } from '@e3dc-react/shell/Hooks'
import { useDPPSettingsFormFullFormInitializer } from '../../hooks'
import { useFarmContext } from 'src/Elements/Farm'

interface Props {
  storageId?: string
  installationId?: string
  dynamicPricesSettings: DynamicPricesModel | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesModel) => void
  hasWritingRights?: boolean
  isOptInRequired?: boolean
}

/**
 * This component renders the dynamic prices section of the system settings
 */
const SettingsFormMinimalVersion: React.FC<Props> = ({
  installationId,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
  hasWritingRights = true,
  isOptInRequired = false,
}) => {
  const { classes } = useStyles()
  const accordionRef = useRef<HTMLDivElement | null>(null)
  const { t } = useCustomTranslation()
  const { isLoading: wallboxesIsLoading, wallboxes } = useWallboxes()
  const { initialValues, validationSchema } = useDPPSettingsFormFullFormInitializer(dynamicPricesSettings)

  const hasNoWallboxes = !wallboxesIsLoading && wallboxes.length === 0
  const isDppWallboxToggleDisabled = hasNoWallboxes && initialValues.dynamicPricesForWallboxesEnabled === false

  const previousIsOptInRequired = usePrevious(isOptInRequired)
  const [hasOptedInChanged, setHasOptedInChanged] = useState(false)
  const { isScrollTarget } = useScrollToHash(accordionRef, SystemSettingHashes.DynamicPowerPriceSettings)

  useEffect(() => {
    if (previousIsOptInRequired !== isOptInRequired) {
      setHasOptedInChanged(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * This minimal version of dynamic power prices does not includes all properties yet.
   * API expects only a reduced payload, created here.
   */
  const onSubmit = (values: DynamicPricesModel): void => {
    const minimalVersionValues: DynamicPricesModel = {
      dynamicPricesForWallboxesEnabled: values.dynamicPricesForWallboxesEnabled,
      priceLimitWallbox: values.priceLimitWallbox,
      energyPriceSurcharge: values.energyPriceSurcharge,
      networkFees: values.networkFees,
      taxes: values.taxes,
    }
    handleDynamicPricesSettingsUpdate(minimalVersionValues)
  }
  const { isDppCountryInstallation } = useCheckInstallationLocation()
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true
  const isDynamicPricesSettingsAvailable = dynamicPricesSettings !== null
  const isAllowedInstallationLocation = isDppCountryInstallation || isDynamicPricesSettingsAvailable

  if (isOptInRequired)
    return (
      <Box ref={accordionRef} sx={{ mb: 1 }}>
        <FormOptIn isServerOffline={isServerOffline} />
      </Box>
    )
  if (!dynamicPricesSettings) return <FormBlankView isAllowedInstallationLocation={isAllowedInstallationLocation} isServerOffline={isServerOffline} />

  const accordionTitle = t('systemSettings.price-based-charging.section-title')

  return (
    <FormikWithExitHandler
      formKey="dynamic-power-price-settings-minimal-version-form"
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <OptionalTooltipWrapper
        hasTooltip={!isAllowedInstallationLocation || isServerOffline}
        placement={'top'}
        titleKey={isServerOffline ? MASTER_IS_OFFLINE_KEY : COUNTRY_NOT_AVAILABLE_KEY}
      >
        <AccordionItem
          ref={accordionRef}
          disabled={!isAllowedInstallationLocation || isServerOffline}
          title={accordionTitle}
          className={classes.accordion}
          expanded={(isScrollTarget && !isServerOffline && (isAllowedInstallationLocation || isOptInRequired)) || hasOptedInChanged}
        >
          <AccordionItemContent variant="paper">
            <SettingsFormContainer model={initialValues}>
              <MinimalVersionFormContent
                installationId={installationId}
                hasWritingRights={hasWritingRights}
                isDppWallboxToggleDisabled={isDppWallboxToggleDisabled}
              />
            </SettingsFormContainer>
          </AccordionItemContent>
        </AccordionItem>
      </OptionalTooltipWrapper>
    </FormikWithExitHandler>
  )
}

export default SettingsFormMinimalVersion
